import { parseCountryCode } from "@/libs/locale/cookie";

// Global-E 가격 마커(`data-ge-price`) KR 가드의 단일 판정. docs/global-e-storefront.md §1.1.
//
// 비-KR + 형식 유효한 commerce country 만 GE 표시(가격 변환) 대상. KR/미설정/garbage → 대상 아님.
// GePrice(`useGeDisplayMarked`)가 이 판정을 공유한다(가격 마커 KR 차단 유일 가드).
// ⚠️ 체크아웃 버튼 라우팅은 더 이상 마커(`data-ge-checkout-button`)로 하지 않는다 — Kyuri 권고로 마커를
//    제거하고 `cart-checkout-button` 이 직접 `/international-checkout` 로 라우팅한다(GE clone/오라우팅
//    제거, §4.9). 라우팅 판정 = `useGeRouted()`(flag ON + 비-KR). carrier 판정은 2026-08-20 폐지.
export function isGeMarkedCountry(countryCode?: string | null): boolean {
  const cc = parseCountryCode(countryCode);
  return !!cc && cc !== "KR";
}

/**
 * **GE 국제 체크아웃으로 가는가** — 라우팅 술어의 **유일한 사본**.
 *
 * 조건은 둘뿐이다: **GE flag ON + 비-KR**(US 포함). carrier 판정(무게·배터리)은 2026-08-20 폐지 —
 * 비-KR 은 전부 GE 결제고, 배터리 3개 이상은 GE Admin 에서 운송사만 바꾼다.
 *
 * ## ★왜 순수 함수인가 (훅이 아니라)
 *
 * 소비처가 둘인데 **한쪽이 훅을 못 쓴다**: 카트는 `useGeRouted()`(훅), 바로구매는
 * `runCheckout`(non-hook, `useCheckout.ts`). 술어를 각자 인라인으로 쓰면 **사본이 둘**이 되고
 * 한쪽만 바뀌어 갈린다 — 카트는 GE / 바로구매는 자체(또는 그 반대).
 *
 * 실제로 **#5233·#5296 에서 두 번 갈렸고**, 그때마다 소스 가드로 막았는데 그 가드가 매번
 * **"이번에 없어지는 기능" 파일에 얹혀 있어 함께 삭제**됐다(#5296 → #5315 에서 재발).
 * 사본을 하나로 만들면 가드 자체가 필요 없다.
 *
 * ## ★KR 불변식
 *
 * `isGeMarkedCountry` 가 `cc !== "KR"` 이다. 이 항이 빠지면 **한국 주문이 이니시스·네이버페이를
 * 우회해 GE 국제 체크아웃으로 흐른다** — 국내 배송인데 국제 통관 경로를 타고 원화 결제도 안 된다.
 * 게이트 중 가장 비싼 회귀라 `__tests__/hooks/useGeRouted.test.tsx` 가 잠근다.
 *
 * `geEnabled` 를 `boolean` 이 아니라 `boolean | null` 로 받는다 — `useIsFeatureOn` 이 로딩 중
 * `null` 을 반환하므로, 호출부마다 `!!` 를 붙이는 대신 여기서 한 번에 처리한다(누락 방지).
 */
export function isGeRoutedFor(
  geEnabled: boolean | null | undefined,
  countryCode?: string | null,
): boolean {
  return !!geEnabled && isGeMarkedCountry(countryCode);
}

// GE 엔진 JS(country/currency 재판별 담당)를 붙이는 **인라인 로더** <script> 의 DOM id.
// (엔진 <script> 자체에는 id 가 없다 — GE snippet 형태, #5837 · script-loader.ts.)
// `GlobalEScripts` 가 `globalEEnabled === true` + 비-webview 일 때만 이 id 로 주입하므로,
// 이 엘리먼트의 존재 여부 = "GE 가 이 페이지에 실제 활성"의 동기적 신호다. `setLocale` 의
// reload 게이트(§3.5)가 이 신호로 GE 재초기화가 필요한지 판정한다. 두 곳이 같은 상수를
// 공유해야 드리프트 시 게이트가 조용히 깨지지 않는다.
export const GLOBALE_SCRIPT_ID = "globaleScript";

/**
 * **`GePrice` 밖의 가격 문구**(「Save $49」 배지처럼 번역 문장 안에 금액이 든 것)의 GE 속성.
 *
 * - GE 대상(`marked`): GE 가 페이지의 가격 문자열을 자동으로 찾아 **가격과 같은 순간에** 바꾼다(dev 실측
 *   2026-10-12 AL: 배지 `Save $49` 가 0.25–1.90초 노출 → 가격 11/11 처리와 동시에 `Save ALL 4,150`).
 *   그 전 `$` 가 보이지 않게 `data-ge-text` 를 붙여 가격과 같은 숨김(flag 대기 · GE 처리 대기)에 묶는다
 *   (`src/styles/globals.css`).
 * - GE 비대상: `data-gem-ignore` 로 GE 변환을 막는다(§4.11 — 마커를 안 붙이는 것만으론 못 막는다).
 */
export const geTextProps = (marked: boolean) =>
  (marked ? { "data-ge-text": "" } : { "data-gem-ignore": "" }) as Record<
    `data-${string}`,
    string
  >;
